import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChefHat, CalendarRange, MessageCircle, Crown, X } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const ICONS_ROUTES = [
  { Icon: Camera, route: "/app/pasto" },
  { Icon: ChefHat, route: "/app/ricette" },
  { Icon: CalendarRange, route: "/app/menu-settimanale" },
  { Icon: MessageCircle, route: "/app" },
];

const STR = {
  de: {
    welcome: "Willkommen bei Pro", tryIt: "Jetzt ausprobieren →", next: "Weiter", go: "Los geht's 🚀",
    steps: [
      { title: "KI-Foto-Analyse", desc: "Fotografiere deine Mahlzeit — die KI erkennt Zutaten, Kalorien und Makros automatisch. Jetzt unbegrenzt." },
      { title: "KI-Rezepte", desc: "Lass dir Rezepte aus deinem Vorrat zaubern — passend zu deinen Zielen, so oft du willst." },
      { title: "Wochenmenü-Generator", desc: "Dein ganzes Wochenmenü inkl. Einkaufsliste auf Knopfdruck — mediterran und auf dich abgestimmt." },
      { title: "KI-Coach", desc: "Stell Fragen, hol dir Feedback oder sprich einfach mit deinem Coach — rund um die Uhr für dich da." },
    ],
  },
  en: {
    welcome: "Welcome to Pro", tryIt: "Try it now →", next: "Next", go: "Let's go 🚀",
    steps: [
      { title: "AI photo analysis", desc: "Snap your meal — the AI recognizes ingredients, calories and macros automatically. Now unlimited." },
      { title: "AI recipes", desc: "Get recipes conjured from your pantry — matching your goals, as often as you like." },
      { title: "Weekly menu generator", desc: "Your whole weekly menu incl. shopping list at the tap of a button — Mediterranean and tailored to you." },
      { title: "AI coach", desc: "Ask questions, get feedback or just talk to your coach — there for you around the clock." },
    ],
  },
  it: {
    welcome: "Benvenuto in Pro", tryIt: "Provalo ora →", next: "Avanti", go: "Si parte 🚀",
    steps: [
      { title: "Analisi foto AI", desc: "Fotografa il tuo pasto — l'AI riconosce ingredienti, calorie e macro automaticamente. Ora senza limiti." },
      { title: "Ricette AI", desc: "Fatti creare ricette dalla tua dispensa — in linea con i tuoi obiettivi, tutte le volte che vuoi." },
      { title: "Generatore menu settimanale", desc: "Tutto il tuo menu settimanale con lista della spesa in un tocco — mediterraneo e su misura per te." },
      { title: "Coach AI", desc: "Fai domande, ricevi feedback o parla semplicemente con il tuo coach — a tua disposizione 24 ore su 24." },
    ],
  },
  fr: {
    welcome: "Bienvenue dans Pro", tryIt: "Essayer maintenant →", next: "Suivant", go: "C'est parti 🚀",
    steps: [
      { title: "Analyse photo IA", desc: "Photographie ton repas — l'IA reconnaît ingrédients, calories et macros automatiquement. Désormais illimité." },
      { title: "Recettes IA", desc: "Fais-toi créer des recettes depuis ton garde-manger — adaptées à tes objectifs, aussi souvent que tu veux." },
      { title: "Générateur de menu hebdo", desc: "Tout ton menu de la semaine avec liste de courses en un geste — méditerranéen et adapté à toi." },
      { title: "Coach IA", desc: "Pose des questions, reçois du feedback ou parle simplement à ton coach — disponible 24 h/24." },
    ],
  },
};

export const triggerProWelcome = () => {
  try { localStorage.setItem("solano_pro_welcome", "pending"); } catch {}
};

/** One-time tour shown after a user becomes Pro (Apple or Web purchase). */
export function ProWelcomeTour() {
  const navigate = useNavigate();
  const { lang } = useT();
  const S = STR[lang] || STR.de;
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (localStorage.getItem("solano_pro_welcome") === "pending") setOpen(true);
    } catch {}
  }, []);

  if (!open) return null;

  const done = () => {
    try { localStorage.setItem("solano_pro_welcome", "done"); } catch {}
    setOpen(false);
  };
  const tryIt = () => { const r = ICONS_ROUTES[step].route; done(); navigate(r); };
  const { Icon } = ICONS_ROUTES[step];
  const { title, desc } = S.steps[step];
  const last = step === ICONS_ROUTES.length - 1;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="pro-welcome-tour">
      <div className="w-full max-w-sm rounded-3xl bg-white dark:bg-[#141819] shadow-2xl overflow-hidden">
        <div className="relative p-6 text-white" style={{ background: "linear-gradient(135deg, #2C5254 0%, #5A8B8F 60%, #1A3A3C 100%)" }}>
          <button onClick={done} data-testid="pro-welcome-close" className="absolute top-3 right-3 p-1.5 rounded-full bg-white/15 active:scale-90 transition">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 text-[#FFD166]">
            <Crown className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest">{S.welcome}</span>
          </div>
          <div className="mt-4 h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center">
            <Icon className="h-7 w-7" />
          </div>
          <h2 className="mt-3 font-heading text-xl font-bold" data-testid="pro-welcome-step-title">{title}</h2>
        </div>
        <div className="p-6 pt-4">
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed min-h-[60px]">{desc}</p>
          <button onClick={tryIt} data-testid="pro-welcome-try-btn"
            className="mt-2 text-sm font-semibold text-[#2C5254] dark:text-[#8FC1C5] active:opacity-70">
            {S.tryIt}
          </button>
          <div className="mt-5 flex items-center justify-between">
            <div className="flex gap-1.5">
              {ICONS_ROUTES.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-[#2C5254] dark:bg-[#8FC1C5]" : "w-1.5 bg-slate-300 dark:bg-slate-600"}`} />
              ))}
            </div>
            <button
              onClick={() => (last ? done() : setStep(step + 1))}
              data-testid="pro-welcome-next-btn"
              className="h-10 px-6 rounded-full bg-gradient-to-br from-[#E8951C] to-[#C97612] text-white text-sm font-bold shadow active:scale-95 transition">
              {last ? S.go : S.next}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
